/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class IndexController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @tracked
  groupName = '';

  @action
  setGroupName(value) {
    this.groupName = value;
  }

  @tracked
  groupDescription = '';

  @action
  setGroupDescription(value) {
    this.groupDescription = value;
  }

  @tracked
  groupId = '';

  @tracked
  groupDialogOpen = false;

  @tracked
  saveInProgress = false;

  deletegroupDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  isSortable = false;

  @tracked
  deleteGroupDialogOpen;

  @tracked
  groupSearchText;

  @tracked
  groupToDelete;

  @tracked
  selectedGroup;

  // toggleDialog opens/closes the group dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('groupDialogOpen');
    if (!this.groupDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleGroupDialog() {
    this.toggleDialog();
  }

  @action
  editGroup(id) {
    this.store.findRecord('people-schema/group', id).then((group) => {
      this.groupName = group.get('name');
      this.groupDescription = group.get('description');
    });
    this.groupId = id;
    this.toggleDialog();
  }

  @action
  saveGroup() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.groupId)) {
      promise = new Promise((resolve) => {
        let groupName = this.groupName;
        let record = this.store.createRecord('people-schema/group', {
          name: groupName,
          key: this.generateKey(groupName),
          description: this.groupDescription,
          sequence: this.get('model.length') + 1,
          personType: this.personType,
          schoolId: this.get('session.school'),
        });

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.groupId;
        this.store.findRecord('people-schema/group', id).then((group) => {
          group.set('name', this.groupName);
          group.set('description', this.groupDescription);

          resolve(group);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.groupDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
          this.model.update();
        });
    });
  }

  @action
  deleteGroup(id) {
    this.errorMessage = '';
    this.store.findRecord('people-schema/group', id).then((group) => {
      this.groupToDelete = group;
    });
    this.deleteGroupDialogOpen = true;
  }

  @action
  isActionDisabled(group) {
    return (
      group.key === 'medicalCondition' ||
      group.key === 'contacts' ||
      group.key === 'siblings'
    );
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        let group = this.groupToDelete;

        // Deletion confirmed; actually delete (or disconnect) the record.
        this.deleteInProgress = true;

        // Delete record (locally + pushed to server).
        return group
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            group.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteGroupDialogOpen = false;
        this.groupToDelete = null;
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      entity.save();
    });
  }

  generateKey(groupName) {
    let base = groupName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_{2,}/g, '_');
    let groups = this.model;
    if (isEmpty(findBy(groups, 'key', base))) {
      return base;
    }

    let suffix = 2;
    while (!isEmpty(findBy(groups, 'key', `${base}_${suffix}`))) {
      suffix++;
    }

    return `${base}_${suffix}`;
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else if (!isEmpty(err.errors[0])) {
      let error = err.errors[0];
      if (!isEmpty(error.detail)) {
        this.errorMessage = error.detail;
      }
    } else {
      this.errorMessage = err.message;
    }
  }

  resetForm() {
    this.groupId = '';
    this.groupName = '';
    this.groupDescription = '';
    this.selectedGroup = null;
    this.groupSearchText = null;
  }
}
